import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { motion } from "framer-motion";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useDoctorMasterData,
  useDeleteDoctorsMasterData,
  useDoctorMasterDataByHCP,
} from "../../hooks/useDoctorMasterData";
import { usePermission } from "../../hooks/usePermission";
import DataTable from "../shared/DataTable";
import EditHCPDialog from "./EditHCP";
import ViewHCP from "./ViewHCP";
import { useCity } from "../../hooks/useCity";
import { useCountry } from "../../hooks/useCountry";
import {
  EditHCPMasterDataPermission,
  DeleteHCPMasterDataPermission,
} from "../../constants/permissions";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";

const CountryNameCell = ({ countryId, countriesMap }) => {
  return countriesMap?.get(countryId) || "";
};

const CityNameCell = ({ cityId, citiesMap }) => {
  return citiesMap?.get(cityId) || "";
};

const HCPTable = () => {
  const { data: canEditHCPMasterData } = usePermission({
    permissionName: EditHCPMasterDataPermission,
  });

  const { data: canDeleteHCPMasterData } = usePermission({
    permissionName: DeleteHCPMasterDataPermission,
  });

  const { data: citiesData } = useCity();
  const { data: countriesData } = useCountry();

  // const { data: hcpData, isLoading } = useDoctorMasterData();
  const deleteHCP = useDeleteDoctorsMasterData();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedHCP, setSelectedHCP] = useState(null);
  const [editFormData, setEditFormData] = useState({
    national_id: "",
    first_name: "",
    family_name: "",
    email: "",
    phone_number: "",
    specialty: "",
    organization: "",
    medical_license: "",
    APO_number: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [hcpData, setHcpData] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [cursorHistory, setCursorHistory] = useState([null]); // New state for cursor history
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Pass the correct cursor to the hook
  const currentCursor = cursorHistory[cursorHistory.length - 1];

  const { data, isLoading, refetch } = useDoctorMasterData(50, currentCursor);

  const { data: searchResult } = useDoctorMasterDataByHCP(searchTerm);

  const citiesMap = useMemo(() => {
    if (!citiesData) return new Map();
    return new Map(citiesData.map((city) => [city.id, city.name]));
  }, [citiesData]);

  const countriesMap = useMemo(() => {
    if (!countriesData) return new Map();
    return new Map(countriesData.map((country) => [country.id, country.name]));
  }, [countriesData]);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionId(null);
  };

  const handleViewClick = (hcp) => {
    setSelectedHCP(hcp);
    setOpenViewDialog(true);
    handleMenuClose();
  };

  const handleViewClose = () => {
    setOpenViewDialog(false);
    setSelectedHCP(null);
  };

  const handleEditClick = (hcp) => {
    setSelectedHCP(hcp);
    setEditFormData(hcp);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
    setSelectedHCP(null);
    setEditFormData({
      national_id: "",
      first_name: "",
      family_name: "",
      email: "",
      phone_number: "",
      specialty: "",
      organization: "",
      medical_license: "",
      APO_number: "",
    });
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setOpenConfirm(true); // open the modal instead of alert
  };

  const confirmDelete = async () => {
    try {
      if (selectedId) {
        await deleteHCP.mutateAsync(selectedId);
        handleMenuClose();
      }
    } catch (error) {
      console.error("Error deleting HCP:", error);
    } finally {
      setOpenConfirm(false);
      setSelectedId(null);
    }
  };

  const cancelDelete = () => {
    setOpenConfirm(false);
    setSelectedId(null);
  };

  const columns = [
    {
      name: "First Name",
      selector: (row) => row.first_name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Family Name",
      selector: (row) => row.family_name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "150px",
    },
    {
      name: "Phone Number",
      selector: (row) => row.phone_number,
      sortable: true,
      width: "150px",
    },
    {
      name: "Specialty",
      selector: (row) => row.specialty,
      sortable: true,
      width: "150px",
    },
    {
      name: "Country",
      selector: (row) => {
        return (
          <CountryNameCell
            countryId={row.country_id}
            countriesMap={countriesMap}
          />
        );
      },
      sortable: true,
      width: "150px",
    },
    {
      name: "City",
      selector: (row) => {
        return <CityNameCell cityId={row.city_id} citiesMap={citiesMap} />;
      },
      sortable: true,
      width: "120px",
    },
    {
      name: "Industry Area",
      selector: (row) => row.industry_area,
      sortable: true,
      width: "150px",
    },
    {
      name: "Medical License",
      selector: (row) => row.medical_license,
      sortable: true,
      width: "160px",
    },
    {
      name: "Organization",
      selector: (row) => row.organization,
      sortable: true,
      width: "150px",
    },
    {
      name: "National ID",
      selector: (row) => row.national_id,
      sortable: true,
      width: "150px",
    },
    {
      name: "APO Number",
      selector: (row) => row.APO_number,
      sortable: true,
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
      width: "100px",
    },
  ];

  useEffect(() => {
    if (data && Array.isArray(data.data)) {
      // If this is the first page (no cursor history), replace the data
      // Otherwise, append to existing data for pagination
      if (cursorHistory.length === 1 && cursorHistory[0] === null) {
        setHcpData(data.data);
      } else {
        setHcpData((prevData) => [
          ...(Array.isArray(prevData) ? prevData : []),
          ...data.data,
        ]);
      }
      setLastVisible(data.lastVisible);
      setHasMore(data.hasMore);
    }
  }, [data, cursorHistory]);

  useEffect(() => {
    const handleSearch = async () => {
      if (searchTerm.trim() === "") {
        setIsSearching(false);
        setHcpData([]);
        refetch();
      } else {
        setIsSearching(true);
        if (searchResult && searchTerm) {
          setHcpData(Array.isArray(searchResult) ? searchResult : []);
          setIsSearching(false);
        }
      }
    };
    handleSearch();
  }, [searchResult, searchTerm, refetch]);

  const handleNextPage = async () => {
    if (hasMore) {
      setIsRefreshing(true);
      setCursorHistory((prevHistory) => [...prevHistory, lastVisible]);
      try {
        await refetch();
      } catch (error) {
        console.error("Error fetching next page:", error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="relative min-h-[400px] bg-white rounded-lg shadow-md">
        <div className="flex justify-end mt-8 mr-10">
          <ShimmerBadge width={250} />
        </div>
        <ShimmerTable row={8} col={8} gap={20} />
      </div>
    );
  }

  return (
    <>
      <div className="m-6 flex justify-between items-center">
        <button
          className="border border-slate-200 p-2 rounded-md text-blue-600 hover:bg-slate-200 font-bold w-52 flex items-center justify-center"
          onClick={handleNextPage}
          disabled={isRefreshing}
        >
          <i
            className={`fa-solid fa-rotate mr-2 ${
              isRefreshing ? "animate-spin" : ""
            }`}
          ></i>
          {isRefreshing ? "Loading More..." : "Reload More Data"}
        </button>

        <div className="relative w-64">
          <i className="fa-solid fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="Search HCPs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-slate-200  p-2 rounded-md w-full focus:outline-none "
          />
          {isSearching && (
            <p className="text-base font-medium text-blueColor mt-2">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                style={{ display: "inline-block" }}
              >
                ⏳
              </motion.span>{" "}
              This may take some time...
            </p>
          )}
        </div>
      </div>

      <DataTable columns={columns} data={hcpData || []} searchable={false} />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() =>
            handleViewClick(hcpData.find((hcp) => hcp.id === selectedActionId))
          }
        >
          <button
            data-track="view-hcp-button"
            data-module="HCPs"
            className="flex items-center"
          >
            <i className="fa-solid fa-eye text-blue-600 mr-2" />
            <h6 className="text-blueColor">View</h6>
          </button>
        </MenuItem>
        {canEditHCPMasterData && (
          <MenuItem
            onClick={() =>
              handleEditClick(
                hcpData.find((hcp) => hcp.id === selectedActionId)
              )
            }
          >
            <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
            <h6 className="text-blueColor">Edit</h6>
          </MenuItem>
        )}
        {canDeleteHCPMasterData && (
          <MenuItem
            data-track="delete-hcp-button"
            data-module="HCPs"
            onClick={() => handleDelete(selectedActionId)}
          >
            <i className="fa-solid fa-trash text-red-600 mr-2" />
            <h6 className="text-blueColor">Delete</h6>
          </MenuItem>
        )}
      </Menu>

      <EditHCPDialog
        open={openEditDialog}
        onClose={handleEditClose}
        hcpData={editFormData}
        onInputChange={(e) =>
          setEditFormData({ ...editFormData, [e.target.name]: e.target.value })
        }
      />

      <ViewHCP
        open={openViewDialog}
        onClose={handleViewClose}
        hcpData={selectedHCP}
      />

      <Dialog
        open={openConfirm}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            padding: 2, // adds padding inside the dialog
            borderRadius: 2, // optional: rounded corners
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: "bold" }}>
          Delete HCP
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "1.3rem" }}>
            Are you sure you want to delete this HCP?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HCPTable;
